import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api'
import { useAuth } from '../../auth'
import { actionButton } from '../ui/variants'

/**
 * The whole of signing up: a handle, a password, and you are in.
 *
 * No email, no verification, no confirmation link. Every step between arriving and placing a bet
 * costs more real people than it stops fake ones, and there is no prize here to farm.
 *
 * The two modes share one form because they take the same two fields. A separate "create account"
 * page for a form with a different button label is a page to maintain for no reader's benefit.
 */
export function SignInPanel() {
  const { signIn, signUp } = useAuth()
  const [creating, setCreating] = useState(false)
  const [handle, setHandle] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Whether the door is open at all. The server can close registration without a release, and a
  // form that only fails on submit would be a worse way to find that out.
  const { data: registration } = useQuery({
    queryKey: ['registration'],
    queryFn: api.registration,
    staleTime: 60_000,
  })

  const signupOpen = registration?.signupOpen ?? true

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      await (creating ? signUp(handle, password) : signIn(handle, password))
    } catch (failure) {
      // The server's own words: "that handle is taken", "a password is at least 8 characters".
      setError(failure instanceof Error ? failure.message : 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm min-w-0 rounded-lg border border-line bg-surface p-4">
      <h3 className="text-lg font-semibold">
        {creating ? 'Create an account' : 'Sign in'}
      </h3>
      <p className="mt-1 text-sm text-ink-soft">
        Play money, no stake and no prize. A handle and a password is all it takes.
      </p>

      <form onSubmit={submit} className="mt-4 space-y-3">
        <label className="block text-sm">
          <span className="text-ink-soft">Handle</span>
          <input
            value={handle}
            onChange={(event) => setHandle(event.target.value)}
            autoComplete="username"
            className="mt-1 min-h-11 w-full rounded border border-line bg-surface-raised px-2 py-1 text-ink"
          />
        </label>

        <label className="block text-sm">
          <span className="text-ink-soft">Password</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={creating ? 'new-password' : 'current-password'}
            className="mt-1 min-h-11 w-full rounded border border-line bg-surface-raised px-2 py-1 text-ink"
          />
        </label>

        {error !== null ? (
          <p className="rounded border border-danger-edge bg-danger-bg px-3 py-2 text-xs text-danger">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          className={actionButton({ className: 'w-full' })}
        >
          {busy ? 'Working…' : creating ? 'Create account' : 'Sign in'}
        </button>
      </form>

      {creating || signupOpen ? (
        <button
          type="button"
          onClick={() => {
            setCreating(!creating)
            setError(null)
          }}
          className="mt-3 text-xs text-ink-faint hover:text-ink"
        >
          {creating ? 'I already have an account' : 'I need an account'}
        </button>
      ) : (
        <p className="mt-3 text-xs text-ink-faint">
          New accounts are closed at the moment.
        </p>
      )}

      {creating ? (
        <p className="mt-3 text-xs text-ink-faint">
          There is no email here, so there is no self-service password reset. If you lose it, ask
          whoever runs the site for a reset link.
        </p>
      ) : null}
    </div>
  )
}
